import { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Download as DownloadIcon } from '@mui/icons-material';
import { startOfDay } from 'date-fns';
import { useEngagementEditor } from './EngagementEditorContext';
import { AdviseMilestone } from '../../../../types/advise';
import { downloadBlob } from '../../../../utils/fileDownload';
import { getDate, isPastDue } from '../../../../utils/helpers';
import { ConfirmDialog } from '../ConfirmDialog';

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: 'default' },
  { value: 'in_progress', label: 'In Progress', color: 'warning' },
  { value: 'complete', label: 'Complete', color: 'success' }
] as const;

const OWNER_OPTIONS = ['Seller', 'Buyer', 'Partner'] as const;

const OWNER_COLORS: Record<string, string> = {
  Seller: 'bg-blue-100 text-blue-800',
  Buyer: 'bg-green-100 text-green-800',
  Partner: 'bg-purple-100 text-purple-800'
};

const DEFAULT_TEMPLATE: Omit<AdviseMilestone, 'id' | 'created_date'>[] = [
  {
    milestone: 'Shortlist Suppliers',
    owner: 'Seller',
    due_date: '',
    status: 'not_started',
    notes: ''
  },
  {
    milestone: 'Schedule Supplier Demos',
    owner: 'Seller',
    due_date: '',
    status: 'not_started',
    notes: ''
  },
  {
    milestone: 'Technical Discovery Sessions',
    owner: 'Buyer',
    due_date: '',
    status: 'not_started',
    notes: ''
  },
  {
    milestone: 'Security & Compliance Review',
    owner: 'Buyer',
    due_date: '',
    status: 'not_started',
    notes: ''
  },
  {
    milestone: 'Pricing Negotiation',
    owner: 'Seller',
    due_date: '',
    status: 'not_started',
    notes: ''
  },
  {
    milestone: 'Business Case Presentation',
    owner: 'Seller',
    due_date: '',
    status: 'not_started',
    notes: ''
  },
  {
    milestone: 'Procurement Approval',
    owner: 'Buyer',
    due_date: '',
    status: 'not_started',
    notes: ''
  },
  {
    milestone: 'Contract Negotiation',
    owner: 'Partner',
    due_date: '',
    status: 'not_started',
    notes: ''
  },
  {
    milestone: 'Implementation Kickoff',
    owner: 'Partner',
    due_date: '',
    status: 'not_started',
    notes: ''
  }
];

const emptyForm = {
  milestone: '',
  owner: 'Seller' as AdviseMilestone['owner'],
  due_date: '',
  status: 'not_started' as AdviseMilestone['status'],
  notes: ''
};

export const MutualActionPlanPanel = () => {
  const { engagement, canEdit, editEngagement } = useEngagementEditor();
  const saveMilestones = useCallback(
    (milestones: AdviseMilestone[]) => editEngagement({ mutual_action_plan: { milestones } }),
    [editEngagement]
  );
  const milestones = useMemo(() => engagement.mutual_action_plan?.milestones || [], [engagement.mutual_action_plan]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdviseMilestone | null>(null);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState(emptyForm);

  const completedCount = milestones.filter((m) => m.status === 'complete').length;
  const progress = milestones.length > 0 ? Math.round((completedCount / milestones.length) * 100) : 0;

  const filtered = useMemo(() => {
    return milestones.filter((m) => {
      if (ownerFilter !== 'all' && m.owner !== ownerFilter) {
        return false;
      }
      if (statusFilter !== 'all' && m.status !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [milestones, ownerFilter, statusFilter]);

  const handleOpenAdd = useCallback(() => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  }, []);

  const handleOpenEdit = useCallback((m: AdviseMilestone) => {
    setForm({
      milestone: m.milestone,
      owner: m.owner,
      due_date: m.due_date,
      status: m.status,
      notes: m.notes
    });
    setEditingId(m.id);
    setShowForm(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!form.milestone.trim()) return;

    let updated: AdviseMilestone[];
    if (editingId) {
      updated = milestones.map((m) => (m.id === editingId ? { ...m, ...form } : m));
    } else {
      const newMilestone: AdviseMilestone = {
        ...form,
        id: crypto.randomUUID(),
        created_date: new Date().toISOString()
      };
      updated = [...milestones, newMilestone];
    }
    saveMilestones(updated);
    setShowForm(false);
  }, [editingId, form, milestones, saveMilestones]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const updated = milestones.filter((m) => m.id !== deleteTarget.id);
    saveMilestones(updated);
    setDeleteTarget(null);
  }, [deleteTarget, milestones, saveMilestones]);

  const handleStatusChange = useCallback(
    (id: string, status: AdviseMilestone['status']) => {
      const updated = milestones.map((m) => (m.id === id ? { ...m, status } : m));
      saveMilestones(updated);
    },
    [milestones, saveMilestones]
  );

  const handleLoadTemplate = useCallback(() => {
    const templateMilestones: AdviseMilestone[] = DEFAULT_TEMPLATE.map((t) => ({
      ...t,
      id: crypto.randomUUID(),
      created_date: new Date().toISOString()
    }));
    saveMilestones([...milestones, ...templateMilestones]);
  }, [milestones, saveMilestones]);

  const handleExport = useCallback(() => {
    // TODO: escape tabs/newlines in milestone fields before joining — unescaped values shift TSV columns.
    const header = 'Milestone\tOwner\tDue Date\tStatus\tNotes';
    const rows = milestones.map((m) => `${m.milestone}\t${m.owner}\t${m.due_date}\t${m.status}\t${m.notes}`);
    const tsv = [header, ...rows].join('\n');
    downloadBlob(
      new Blob([tsv], { type: 'text/tab-separated-values' }),
      `action-plan-${engagement.engagement_name}.tsv`
    );
  }, [milestones, engagement.engagement_name]);

  const isDueSoon = (dueDate: string) => {
    if (!dueDate) return false;
    const d = getDate(dueDate);
    const now = new Date();
    return d >= startOfDay(now) && d.getTime() - now.getTime() < 3 * 24 * 60 * 60 * 1000;
  };

  return (
    <Box className="flex flex-col gap-4">
      {/* Progress & Controls */}
      <Card variant="outlined">
        <CardContent>
          <Box className="flex items-center justify-between mb-2">
            <Typography variant="subtitle2">
              Progress: {completedCount} / {milestones.length}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary">
              {progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mb: 2 }}
          />

          <Box className="flex flex-wrap items-center gap-2">
            <Select
              size="small"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              sx={{ minWidth: 120 }}>
              <MenuItem value="all">All Owners</MenuItem>
              {OWNER_OPTIONS.map((o) => (
                <MenuItem
                  key={o}
                  value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 140 }}>
              <MenuItem value="all">All Statuses</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem
                  key={s.value}
                  value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>

            <Box className="flex-1" />

            {milestones.length > 0 && (
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExport}>
                Export
              </Button>
            )}

            {canEdit && milestones.length === 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleLoadTemplate}>
                Load Template
              </Button>
            )}

            {canEdit && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}>
                Add Milestone
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Milestones Table */}
      {filtered.length === 0 ? (
        <Alert severity="info">
          {milestones.length === 0
            ? 'No milestones yet. Add one or load a template.'
            : 'No milestones match the current filters.'}
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Milestone</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notes</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((m) => (
                <TableRow
                  key={m.id}
                  hover>
                  <TableCell>
                    <Typography variant="body2">{m.milestone}</Typography>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded ${OWNER_COLORS[m.owner] || ''}`}>{m.owner}</span>
                  </TableCell>
                  <TableCell>
                    {m.due_date ? (
                      <Box className="flex items-center gap-1">
                        <Typography variant="body2">{getDate(m.due_date).toLocaleDateString()}</Typography>
                        {m.status !== 'complete' && isPastDue(m.due_date) && (
                          <Chip
                            label="Overdue"
                            size="small"
                            color="error"
                          />
                        )}
                        {m.status !== 'complete' && isDueSoon(m.due_date) && (
                          <Chip
                            label="Due Soon"
                            size="small"
                            color="warning"
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary">
                        No date
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Select
                        size="small"
                        value={m.status}
                        onChange={(e) => handleStatusChange(m.id, e.target.value as AdviseMilestone['status'])}
                        sx={{ minWidth: 130 }}>
                        {STATUS_OPTIONS.map((s) => (
                          <MenuItem
                            key={s.value}
                            value={s.value}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Chip
                        label={STATUS_OPTIONS.find((s) => s.value === m.status)?.label || m.status}
                        size="small"
                        color={
                          (STATUS_OPTIONS.find((s) => s.value === m.status)?.color || 'default') as
                            | 'default'
                            | 'warning'
                            | 'success'
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className="max-w-[200px] truncate">
                      {m.notes || '—'}
                    </Typography>
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(m)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(m)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={showForm}
        onClose={() => setShowForm(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{editingId ? 'Edit Milestone' : 'Add Milestone'}</DialogTitle>
        <DialogContent>
          <Box className="flex flex-col gap-3 mt-2">
            <TextField
              label="Milestone"
              value={form.milestone}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  milestone: e.target.value
                }))
              }
              fullWidth
              required
            />
            <Select
              value={form.owner}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  owner: e.target.value as AdviseMilestone['owner']
                }))
              }
              fullWidth
              size="small">
              {OWNER_OPTIONS.map((o) => (
                <MenuItem
                  key={o}
                  value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
            <TextField
              label="Due Date"
              type="date"
              value={form.due_date}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  due_date: e.target.value
                }))
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Select
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  status: e.target.value as AdviseMilestone['status']
                }))
              }
              fullWidth
              size="small">
              {STATUS_OPTIONS.map((s) => (
                <MenuItem
                  key={s.value}
                  value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  notes: e.target.value
                }))
              }
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowForm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.milestone.trim()}>
            {editingId ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Milestone"
        message={`Delete "${deleteTarget?.milestone}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};
